<template>
    <modal
        :active="active"
        @closed="$emit('closed', true)"
        :title="title()"
        :large="true"
    >
        <div @click="contactSelectOpen = false">
            <div class="columns is-tablet">
                <form-field
                    class="column"
                    label="Navn"
                    :error="error.name"
                >
                    <input-text
                        v-model="form.name"
                        :error="error.name"
                    />
                </form-field>

                <form-field
                    class="column"
                    label="KM (tur/retur)"
                    :error="error.kilometers"
                >
                    <input-text
                        type="number"
                        v-model="form.kilometers"
                        :error="error.kilometers"
                    />
                </form-field>
            </div>

            <div class="columns is-tablet">
                <form-field
                    class="column"
                    label="Adresse"
                    :error="error.address"
                >
                    <input-text
                        v-model="form.address"
                        :error="error.address"
                    />
                </form-field>

                <form-field
                    class="column"
                    label="Postnummer"
                    :error="error.zipcode"
                >
                    <input-text
                        v-model="form.zipcode"
                        :error="error.zipcode"
                    />
                </form-field>
            </div>

            <div class="columns is-tablet">
                <form-field
                    class="column"
                    label="By"
                    :error="error.city"
                >
                    <input-text
                        v-model="form.city"
                        :error="error.city"
                    />
                </form-field>

                <form-field
                    class="column"
                    label="Land"
                    :error="error.country"
                >
                    <input-text
                        v-model="form.country"
                        :error="error.country"
                    />
                </form-field>
            </div>

            <div class="columns is-tablet">
                <form-field
                    class="column"
                    label="Telefon"
                    :error="error.phonenumber"
                >
                    <input-text
                        v-model="form.phonenumber"
                        :error="error.phonenumber"
                    />
                </form-field>
            </div>

            <div class="contacts-section">
                <h4 class="is-size-4">Kontaktpersoner</h4>

                <div
                    v-if="selectableContacts.length > 0"
                    class="dropdown is-up"
                    :class="{'is-active': contactSelectOpen}"
                    @click.stop="contactSelectOpen = !contactSelectOpen"
                >
                    <div class="dropdown-trigger">
                        <button class="button" aria-haspopup="true" aria-controls="dropdown-menu">
                        <span>Tilføj Kontaktperson</span>
                        <span class="icon is-small">
                            <i class="fas fa-angle-up" aria-hidden="true"></i>
                        </span>
                        </button>
                    </div>

                    <div class="dropdown-menu" id="dropdown-menu" role="menu">
                        <div v-for="contact in selectableContacts"
                            :key="contact.id"
                            class="dropdown-content"
                            @click="addContact(contact)"
                        >
                            <a class="dropdown-item" v-text="contact.name" />
                        </div>
                    </div>
                </div>
            </div>

            <div class="contacts">
                <div v-for="contact in form.contacts"
                    :key="contact.id"
                    class="contact"
                >
                    {{ contact.name }}

                    <div class="actions">
                        <a
                            class="button is-small"
                            :class="contact.primary ? 'is-primary' : 'is-white'"
                            @click="togglePrimaryContact(contact.id)"
                        >
                            Primær
                        </a> |

                        <a
                            class="icon has-text-danger"
                            @click="removeContact(contact.id)"
                        >
                            <i class="fas fa-trash-alt" />
                        </a>
                    </div>
                </div>

                <div v-if="form.contacts.length === 0">Ingen kontaktpersoner tilføjet</div>
            </div>
        </div>

        <template v-slot:footer class="has-space-between">
            <div class="footer-spacer">
                <div>
                    <button class="button is-danger is-flex-left" @click="reset">Nulstil</button>
                    <button class="button is-default" @click="$emit('closed', true)">Annuller</button>
                    <button class="button is-primary" @click="submit">{{ saveButtonText }}</button>
                </div>

                <button class="button is-danger" @click="remove" v-if="isEdit">Slet</button>
            </div>
        </template>
    </modal>
</template>

<script>
import Modal from '@elems/modal'
import { FormField, InputText, InputCheckbox, InputSelect } from '@elems/form'

export default {
    components: {
        Modal,
        FormField,
        InputText,
        InputSelect,
        InputCheckbox
    },
    props: {
        active: Boolean,
        customerId: String,
        location: Object,
        contacts: Array,
    },
    data() {
        return {
            form: {
                name: '',
                address: '',
                zipcode: '',
                city: '',
                country: '',
                phonenumber: '',
                kilometers: 0,
                contacts: []
            },
            error: [],
            contactSelectOpen: false
        }
    },

    watch: {
        active() {
            this.reset()
        },
        location() {
            this.reset()
        }
    },

    computed: {
        isEdit() {
            return this.location !== null
        },
        saveButtonText() {
            return this.isEdit ? 'Gem' : 'Opret'
        },
        selectableContacts() {
            return this.contacts.filter(contact => {
                return !this.form.contacts.find(c => c.id === contact.id)
            })
        }
    },

    methods: {
        title() {
            return this.isEdit
                ? 'Rediger lokation'
                : 'Opret lokation'
        },

        submit() {
            return this.isEdit ? this.update() : this.create()
        },

        create() {
            const contacts = this.form.contacts.map(({ id, primary }) => ({ id, primary }))

            window.axios.post(`/api/customer/${this.customerId}/locations`, { ...this.form, contacts })
                .then(({data}) => {
                    this.$emit('closed')
                    this.$emit('created', data)
                    this.$store.dispatch('addLocation', data)
                    this.$store.dispatch('setSuccess', 'Lokationen blev oprettet')
                    this.reset()
                })
                .catch(error => {
                    if (error.response && error.response.status === 422) {
                        this.error = error.response.data.errors
                        this.detailsOpen = true
                    }
                })
        },

        update() {
            const contacts = this.form.contacts.map(({ id, primary }) => ({ id, primary }))

            window.axios.put(`/api/customer/${this.customerId}/locations/${this.location.id}`, { ...this.form, contacts })
                .then(({data}) => {
                    this.$emit('closed')
                    this.$store.dispatch('updateLocation', data)
                    this.$store.dispatch('setSuccess', 'Lokationen blev opdateret')
                    this.reset()
                })
                .catch(error => {
                    if (error.response && error.response.status === 422) {
                        this.error = error.response.data.errors
                        this.detailsOpen = true
                    }
                })
        },

        reset() {
            if (this.isEdit) {
                this.form = {
                    name: this.location.name,
                    address: this.location.address,
                    zipcode: this.location.zipcode,
                    city: this.location.city,
                    country: this.location.country,
                    phonenumber: this.location.phonenumber,
                    kilometers: this.location.kilometers,
                    contacts: this.location.contacts
                        .map(({id, name, pivot: {primary}}) => {
                            return {id, name, primary}
                        })
                }
            } else {
                this.form = {
                    name: '',
                    address: '',
                    zipcode: '',
                    city: '',
                    country: 'DK',
                    phonenumber: '',
                    kilometers: 0,
                    contacts: []
                }
            }
            this.error = []
        },

        remove() {
            if (!confirm('Er du sikker på du vil slette ' + this.form.name + '?')) {
                return
            }

            window.axios.delete(`/api/customer/${this.customerId}/locations/${this.location.id}`)
                .then(() => {
                    this.$emit('closed')
                    this.$store.dispatch('setSuccess', 'Lokationen blev slettet')
                })
                .catch(error => {
                    this.$store.dispatch('setError', 'Lokationen kunne ikke slettes')
                })
        },

        addContact({ id, name }) {
            this.form.contacts.push({ id, name, primary: false })
        },

        togglePrimaryContact(id) {
            this.form.contacts = this.form.contacts
                .map(contact => {
                    let primary = contact.primary ? false : contact.primary
                    if (contact.id === id) {
                        primary = !contact.primary
                    }

                    return { ...contact, primary }
                })
        },

        removeContact(id) {
            this.form.contacts = this.form.contacts
                .filter(contact => contact.id !== id)
        }
    }
}
</script>

<style lang="scss" scoped>
.footer-spacer {
    width: 100%;
    display: flex;
    flex-direction:row-reverse;
    justify-content: space-between;
}

.column {
    padding: 0 0.75rem;
}

.contacts-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 2px solid whitesmoke;
    margin: 20px 0;
    padding: 10px 0;
}

.contacts {

    .contact {
        display: flex;
        justify-content: space-between;
        padding-bottom: 10px;
        margin-bottom: 10px;
        border-bottom: 2px solid #ececec;
    }
}
</style>
